import { Component, Input, ChangeDetectionStrategy } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';

/**
 * A single status row rendered by {@link StatusBreakdownComponent}.
 */
export interface StatusBreakdownItem {
  /** Display name for the status (e.g. "Accepted"). */
  label: string;
  /** Count for this status. Segments with a count of 0 are omitted from the bar. */
  count: number;
  /**
   * Any CSS color for the bar segment and its legend dot — hex, rgb(), or a
   * CSS custom property. The consuming feature owns its own palette, keeping
   * this component decoupled from any specific status set.
   */
  color: string;
}

/**
 * LMC Status Breakdown
 *
 * A compact, reusable way to summarise a set of status counts as a stacked
 * proportion bar plus an aligned legend. Numbers use tabular figures and the
 * legend tracks are uniform, so counts stay aligned regardless of their digit
 * width. The legend fits as many columns as its container allows and wraps the
 * rest; set the `--sb-legend-min` custom property to change the column width
 * (defaults to 140px).
 *
 * Fully data-driven and palette-agnostic: pass `items` (label + count + color)
 * and, optionally, a `total`. Reuse it for any status/category summary.
 *
 * @example
 * <lmc-status-breakdown
 *   [items]="[
 *     { label: 'Accepted', count: 95, color: '#4caf50' },
 *     { label: 'Returned', count: 4,  color: '#e57373' }
 *   ]"
 *   [total]="128">
 * </lmc-status-breakdown>
 */
@Component({
  standalone: true,
  selector: 'lmc-status-breakdown',
  templateUrl: './status-breakdown.component.html',
  styleUrls: ['./status-breakdown.component.scss'],
  imports: [CommonModule, MatTooltipModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class StatusBreakdownComponent {
  /** Status rows to display. */
  @Input() items: StatusBreakdownItem[] = [];

  /** Headline total. When null, the sum of every item count is used. */
  @Input() total: number | null = null;

  /** Label shown beside the total. */
  @Input() totalLabel = 'Total';

  /** Whether to render the headline total row. */
  @Input() showTotal = true;

  /**
   * Show zero-count rows in the legend (dimmed). The proportion bar always
   * excludes zero-count statuses regardless of this flag.
   */
  @Input() showZeros = true;

  /** Total shown in the headline: the explicit `total` or the summed counts. */
  get displayTotal(): number {
    return this.total ?? this.items.reduce((sum, item) => sum + (item.count || 0), 0);
  }

  /** Non-zero items — the only ones drawn as bar segments. */
  get segments(): StatusBreakdownItem[] {
    return this.items.filter((item) => item.count > 0);
  }

  /** Items shown in the legend, honouring {@link showZeros}. */
  get legendItems(): StatusBreakdownItem[] {
    return this.showZeros ? this.items : this.segments;
  }
}